import React, { useContext, useState } from "react";
import { IGoal } from "../../../../types/dashboard";
import { AppContext } from "../../../../appContext";

import './GoalCard.css';

type ConfirmationType = 'delete' | 'reset' | null;

interface IGoalCardProps {
    goal: IGoal;
    colorIndex: number;
    onEdit: () => void;
    onDelete: () => void;
    onReset: () => void;
    onAdjustDays: (newDays: number) => void;
}

const PASTEL_COLORS = [
    '#B8E6F0', // Light blue
    '#FFE4CC', // Light orange
    '#E4D4F4', // Light purple
    '#C8F4D4', // Light green
    '#FFD4E5', // Light pink
    '#FFF4CC', // Light yellow
    '#D4E5FF', // Light periwinkle
    '#FFE0D4', // Light peach
];

export const GoalCard: React.FC<IGoalCardProps> = ({ 
    goal, 
    colorIndex, 
    onEdit, 
    onDelete, 
    onReset,
    onAdjustDays
}) => {
    const { translate } = useContext(AppContext);
    const [confirmationType, setConfirmationType] = useState<ConfirmationType>(null);
    
    const calculateDays = () => {
        const start = new Date(goal.startDate);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - start.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const handleIncrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        const currentDays = calculateDays();
        onAdjustDays(currentDays + 1);
    };

    const handleDecrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        const currentDays = calculateDays();
        if (currentDays > 0) {
            onAdjustDays(currentDays - 1);
        }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmationType('delete');
    };

    const handleResetClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmationType('reset');
    };

    const handleConfirm = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (confirmationType === 'delete') {
            onDelete();
        } else if (confirmationType === 'reset') {
            onReset();
        }
        setConfirmationType(null);
    };

    const handleCancel = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmationType(null);
    };

    const backgroundColor = PASTEL_COLORS[colorIndex % PASTEL_COLORS.length];
    const days = calculateDays();

    return (
        <div className="goal_card" style={{ backgroundColor }} onClick={onEdit}>
            <div className="goal_card_content">
                <div className="goal_card_top">
                    <div className="goal_card_left">
                        <div className="goal_card_days">{days}</div>
                        <div className="goal_card_arrows" onClick={(e) => e.stopPropagation()}>
                            <button 
                                className="goal_card_arrow_btn"
                                onClick={handleIncrement}
                                title="Increase days"
                            >
                                ▲
                            </button>
                            <button 
                                className="goal_card_arrow_btn"
                                onClick={handleDecrement}
                                title="Decrease days"
                            >
                                ▼
                            </button>
                        </div>
                    </div>
                    <div className="goal_card_emoji">{goal.emoji}</div>
                </div>
                <div className="goal_card_title">{goal.title}</div>
            </div>
            <div className="goal_card_actions" onClick={(e) => e.stopPropagation()}>
                <button 
                    className="goal_card_reset_btn"
                    onClick={handleResetClick}
                    title={translate('goals_reset') || 'Reset to day 0'}
                >
                    ↻
                </button>
                <button 
                    className="goal_card_delete_btn"
                    onClick={handleDeleteClick}
                    title={translate('goals_delete') || 'Delete goal'}
                >
                    ×
                </button>
            </div>
            
            {confirmationType && (
                <div className="goal_card_confirm_overlay" onClick={handleCancel}>
                    <div className="goal_card_confirm_popup" onClick={(e) => e.stopPropagation()}>
                        <div className="goal_card_confirm_message">
                            {confirmationType === 'delete' 
                                ? (translate('goals_confirm_delete') || 'Delete this goal?')
                                : (translate('goals_confirm_reset') || 'Reset this goal to day 0?')
                            }
                        </div>
                        <div className="goal_card_confirm_buttons">
                            <button 
                                className="goal_card_confirm_cancel"
                                onClick={handleCancel}
                            >
                                {translate('common_cancel') || 'Cancel'}
                            </button>
                            <button 
                                className={`goal_card_confirm_action ${confirmationType === 'delete' ? 'goal_card_confirm_action--danger' : ''}`}
                                onClick={handleConfirm}
                            >
                                {confirmationType === 'delete' 
                                    ? (translate('common_delete') || 'Delete')
                                    : (translate('common_reset') || 'Reset')
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
